import {
  Add as AddIcon,
  Article as ArticleIcon,
  CheckCircle as CheckCircleIcon,
  Checklist as ChecklistIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Send as SendIcon,
  ThumbUp as ThumbUpIcon,
  Upload as UploadIcon,
  Save as SaveIcon,
  AccessTime as AccessTimeIcon,
  Assignment as TotalIcon,
  HourglassEmpty as PendingIcon,
  TaskAlt as CompletedIcon,
  CalendarMonth as CalendarMonthIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Avatar,
  AvatarGroup,
  TextField,
  Tooltip,
  Typography,
  Switch,
  Stack,
  Pagination,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import ContentTrackingService from '../components/backendservices/ContentTrackingService';
import ChecklistDialog from '../components/ChecklistDialog';
import Loader from '../components/Loader';
import {
  ChecklistItem,
  ContentTask,
  CreateTaskRequest,
  UpdateTaskRequest,
  User,
} from '../types/ContentTracking';
import { capitalizeFirstLetter } from '../utils/textUtils';

const ContentTracking: React.FC = () => {
  const [tasks, setTasks] = useState<ContentTask[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    totalTasks: 0,
    totalPages: 1,
    limit: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openChecklistDialog, setOpenChecklistDialog] = useState(false);
  const [deleteTaskDialogOpen, setDeleteTaskDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<ContentTask | null>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [displayDialogMode, setDisplayDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState<CreateTaskRequest>({
    title: '',
    assignedTo: [],
    dueDate: '',
    category: 'Blog',
    content: '',
    bypassUpload: true,
    recurrenceType: 'no_change',
  });

  const [viewTaskChecklist, setViewTaskChecklist] = useState<ChecklistItem[]>([]);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [postedUrlInput, setPostedUrlInput] = useState('');
  const [selectedUserFilter, setSelectedUserFilter] = useState<string>('');
  const [selectedPeriodFilter, setSelectedPeriodFilter] = useState<'all' | 'weekly' | 'monthly'>(
    'all'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit mode state for Task Details in view section
  const [isEditingViewTaskDetails, setIsEditingViewTaskDetails] = useState(false);
  const [editingViewSeoImage, setEditingViewSeoImage] = useState<File | null>(null);
  const [editingViewDriveUrl, setEditingViewDriveUrl] = useState('');

  const userRole = Cookies.get('role');
  const isSuperAdmin = userRole === 'superadmin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;
  const isAdminOrSales = userRole === 'admin' || userRole === 'sales' || isSuperAdmin;
  const isSales = userRole === 'sales';

  console.log('User role:', userRole, 'isSuperAdmin:', isSuperAdmin, 'isAdmin:', isAdmin);

  useEffect(() => {
    fetchTasks(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserFilter, selectedPeriodFilter]);

  useEffect(() => {
    // Only fetch users if user is admin or superadmin
    if (isAdmin) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTasks = async (page = 1) => {
    try {
      setLoadingTasks(true);
      const response = await ContentTrackingService.getAllTasks(
        page,
        10,
        selectedUserFilter,
        selectedPeriodFilter
      );
      if (response.status) {
        setTasks(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
          setCurrentPage(response.pagination.currentPage);
        }
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      showSnackbar('Failed to fetch tasks', 'error');
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      console.log('Fetching users...');
      const response = await ContentTrackingService.getUsers();
      console.log('Users response:', response);

      if (response.status && Array.isArray(response.data)) {
        setUsers(response.data);
        console.log('Users set successfully:', response.data.length, 'users');
      } else {
        console.error('Invalid users response format:', response);
        showSnackbar('Failed to fetch users: Invalid response format', 'error');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCreateTask = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const response = await ContentTrackingService.createTask(formData);
      if (response.status) {
        showSnackbar('Task created successfully', 'success');
        setOpenDialog(false);
        resetForm();
        fetchTasks(1);
      }
    } catch (error) {
      console.error('Error creating task:', error);
      showSnackbar('Failed to create task', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTask = async () => {
    if (!editingTask || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const updateData: UpdateTaskRequest = {
        title: formData.title,
        assignedTo: formData.assignedTo,
        dueDate: formData.dueDate,
        category: formData.category,
        content: formData.content,
        bypassUpload: formData.bypassUpload,
        recurrenceType: formData.recurrenceType,
      };

      const response = await ContentTrackingService.updateTask(editingTask._id, updateData);
      if (response.status) {
        showSnackbar('Task updated successfully', 'success');
        setOpenDialog(false);
        setEditingTask(null);
        resetForm();
        fetchTasks(currentPage);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      showSnackbar('Failed to update task', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTaskClick = (taskId: string) => {
    setTaskToDelete(taskId);
    setDeleteTaskDialogOpen(true);
  };

  const handleDeleteTaskConfirm = async () => {
    if (!taskToDelete) return;

    try {
      const response = await ContentTrackingService.deleteTask(taskToDelete);
      if (response.status) {
        showSnackbar('Task deleted successfully', 'success');
        fetchTasks(currentPage);
        setDeleteTaskDialogOpen(false);
        setTaskToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      showSnackbar('Failed to delete task', 'error');
    }
  };

  const handleDeleteTaskCancel = () => {
    setDeleteTaskDialogOpen(false);
    setTaskToDelete(null);
  };

  const handleApproveTask = async (taskId: string) => {
    try {
      await ContentTrackingService.approveTask(taskId, true);
      showSnackbar('Task approved successfully', 'success');
      fetchTasks(currentPage);
    } catch (error) {
      console.error('Error approving task:', error);
      showSnackbar('Failed to approve task', 'error');
    }
  };

  const handleEditTask = (task: ContentTask) => {
    setEditingTask(task);
    setDialogMode('edit');
    setDisplayDialogMode('edit');
    setFormData({
      title: task.title,
      assignedTo: task.assignedTo.map((user) => user._id),
      dueDate: task.dueDate.split('T')[0],
      category: task.category,
      content: task.content,
      bypassUpload: task.bypassUpload || false,
      recurrenceType: task.recurrenceType || 'no_change',
    });
    setOpenDialog(true);
  };

  const fetchChecklistForView = async (task: ContentTask) => {
    try {
      setLoadingChecklist(true);
      // First try to get checklists from API for this category
      const response = await ContentTrackingService.getActiveChecklistsByCategory(task.category);

      if (response.status && response.data.length > 0) {
        // Convert API checklists to checklist items
        const apiChecklistItems: ChecklistItem[] = response.data.map((item) => ({
          id: item._id,
          title: item.title,
          description: item.description,
          isCompleted: false,
        }));

        // Use existing task checklist if available, otherwise use API checklists
        const taskChecklist =
          task.checklist && task.checklist.length > 0 ? task.checklist : apiChecklistItems;

        // Mark as completed if task is pending_approval or approved
        const markedChecklist =
          task.status === 'pending_approval' || task.status === 'approved'
            ? taskChecklist.map((item) => ({
                ...item,
                isCompleted: true,
                completedAt: item.completedAt || new Date().toISOString(),
              }))
            : taskChecklist;

        setViewTaskChecklist(markedChecklist);
      } else {
        // Fallback to task checklist or empty array
        const taskChecklist = task.checklist && task.checklist.length > 0 ? task.checklist : [];

        // Mark as completed if task is pending_approval or approved
        const markedChecklist =
          task.status === 'pending_approval' || task.status === 'approved'
            ? taskChecklist.map((item) => ({
                ...item,
                isCompleted: true,
                completedAt: item.completedAt || new Date().toISOString(),
              }))
            : taskChecklist;

        setViewTaskChecklist(markedChecklist);
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
      // Fallback to task checklist or empty array
      const taskChecklist = task.checklist && task.checklist.length > 0 ? task.checklist : [];
      setViewTaskChecklist(taskChecklist);
    } finally {
      setLoadingChecklist(false);
    }
  };

  const handleViewTask = (task: ContentTask) => {
    setEditingTask(task);
    setDialogMode('view');
    setDisplayDialogMode('view');
    setFormData({
      title: task.title,
      assignedTo: task.assignedTo.map((user) => user._id),
      dueDate: task.dueDate.split('T')[0],
      category: task.category,
      content: task.content,
      recurrenceType: task.recurrenceType || 'no_change',
    });
    setPostedUrlInput(task.postedUrl || '');
    setIsEditingViewTaskDetails(false);
    setEditingViewSeoImage(null);
    setEditingViewDriveUrl(task.driveUrl || '');
    setOpenDialog(true);
    // Fetch checklist when viewing task
    fetchChecklistForView(task);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      assignedTo: [],
      dueDate: '',
      category: 'Blog',
      content: '',
      bypassUpload: true,
      recurrenceType: 'no_change',
    });
    setEditingTask(null);
    setDialogMode('create');
    setDisplayDialogMode('create');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    // Delay resetForm to avoid showing Cancel button and title change during close animation
    setTimeout(() => {
      resetForm();
      setViewTaskChecklist([]);
      setPostedUrlInput('');
      setIsEditingViewTaskDetails(false);
      setEditingViewSeoImage(null);
      setEditingViewDriveUrl('');
    }, 300); // Match dialog close animation duration
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // Handlers for editing Task Details in view section
  const handleEditViewImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showSnackbar('Please select an image file', 'error');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('Image file size must be less than 5MB', 'error');
        return;
      }

      setEditingViewSeoImage(file);
    }
  };

  const handleEditViewTaskDetails = () => {
    setIsEditingViewTaskDetails(true);
    setEditingViewDriveUrl(editingTask?.driveUrl || '');
    setEditingViewSeoImage(null);
  };

  const handleCancelEditViewTaskDetails = () => {
    setIsEditingViewTaskDetails(false);
    setEditingViewDriveUrl(editingTask?.driveUrl || '');
    setEditingViewSeoImage(null);
  };

  const handleSaveViewTaskDetails = async () => {
    if (!editingTask) return;

    // Check if at least one field has been changed
    const hasChanges = editingViewSeoImage || editingViewDriveUrl !== (editingTask.driveUrl || '');

    if (!hasChanges) {
      showSnackbar('No changes to save', 'success');
      setIsEditingViewTaskDetails(false);
      return;
    }

    try {
      setLoadingChecklist(true);
      const response = await ContentTrackingService.updateTaskDetails(editingTask._id, {
        seoImage: editingViewSeoImage || undefined,
        driveUrl: editingViewDriveUrl || undefined,
      });
      showSnackbar('Task details updated successfully', 'success');
      setIsEditingViewTaskDetails(false);
      // Update editingTask with the response data
      if (response.status && response.data) {
        setEditingTask(response.data);
      }
      // Refresh tasks list to reflect changes
      await fetchTasks();
    } catch (error: any) {
      console.error('Error updating task details:', error);
      if (error.response?.data?.message) {
        showSnackbar(error.response.data.message, 'error');
      } else {
        showSnackbar('Failed to update task details', 'error');
      }
    } finally {
      setLoadingChecklist(false);
    }
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#6871d1', '#ff9f43', '#00d2d3', '#54a0ff', '#5f27cd', '#ff6b6b'];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return { bg: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', border: 'rgba(76, 175, 80, 0.2)' };
      case 'pending':
      case 'pending_approval':
        return { bg: 'rgba(255, 193, 7, 0.1)', color: '#ffc107', border: 'rgba(255, 193, 7, 0.2)' };
      default:
        return {
          bg: 'rgba(104, 113, 209, 0.1)',
          color: '#aeb3e9',
          border: 'rgba(104, 113, 209, 0.2)',
        };
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return '#4caf50'; // Green
      case 'approved':
        return '#4caf50'; // Green
      case 'pending':
        return '#ffc107'; // Yellow/Amber
      case 'pending_approval':
        return '#ffc107'; // Yellow/Amber
      case 'in-progress':
        return '#6871d1'; // Blue (brand color)
      default:
        return '#aaa'; // Gray (default)
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return dayjs(dateString).format('MMM DD, YYYY');
    } catch {
      return dateString;
    }
  };

  // Filter tasks based on selected user and period
  // Now handled by backend
  const filteredTasks = tasks;

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant='h4'
          component='h1'
          sx={{
            fontWeight: 800,
            color: '#6871d1',
            letterSpacing: 1,
            textShadow: '0 2px 8px rgba(104,113,209,0.10)',
          }}
        >
          Task Tracking
        </Typography>
        {(isSuperAdmin || isSales) && (
          <Button
            disabled={isSales}
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setDialogMode('create');
              setDisplayDialogMode('create');
              setOpenDialog(true);
            }}
            sx={{
              backgroundColor: '#6871d1',
              color: '#fff',
              py: 1.5,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
              transition: 'all 0.3s ease',
              fontWeight: 500,
              textTransform: 'capitalize',
              px: 3,
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#5a63bb',
                boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
              },
              '&:disabled': {
                backgroundColor: '#aeb3e9',
                color: '#fff',
                opacity: 0.7,
              },
            }}
          >
            Create Task
          </Button>
        )}
      </Box>

      {/* Tasks Content */}
      {loadingTasks ? (
        <Loader />
      ) : (
        <>
          {/* Stats Section - Streamlined */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {[
              {
                title: 'Total Tasks',
                value: pagination.totalTasks,
                icon: <TotalIcon />,
                color: '#6871d1',
                bg: 'linear-gradient(135deg, rgba(104, 113, 209, 0.1) 0%, rgba(104, 113, 209, 0.05) 100%)',
              },
              {
                title: 'Pending',
                value: tasks.filter(
                  (t) => t.status === 'pending' || t.status === 'pending_approval'
                ).length,
                icon: <PendingIcon />,
                color: '#ffc107',
                bg: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%)',
              },
              {
                title: 'Completed',
                value: tasks.filter((t) => t.status === 'completed' || t.status === 'approved')
                  .length,
                icon: <CompletedIcon />,
                color: '#4caf50',
                bg: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
              },
            ].map((stat, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 4,
                    background: stat.bg,
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px -4px ${stat.color}15`,
                      borderColor: `${stat.color}30`,
                    },
                  }}
                >
                  {/* Decorative Circle */}
                  <Box
                    sx={{
                      position: 'absolute',
                      right: -10,
                      top: -10,
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: stat.color,
                      opacity: 0.05,
                      pointerEvents: 'none',
                    }}
                  />

                  <Box>
                    <Typography variant='body2' sx={{ color: '#8f90a6', fontWeight: 600, mb: 0.5 }}>
                      {stat.title}
                    </Typography>
                    <Typography variant='h3' sx={{ fontWeight: 800, color: '#fff' }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${stat.color}15`,
                      color: stat.color,
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Controls Bar - Clean & Professional */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 4,
              p: 1,
            }}
          >
            <Box>
              <Typography variant='h6' sx={{ color: '#fff', fontWeight: 700 }}>
                All Tasks
                <Box
                  component='span'
                  sx={{
                    ml: 1.5,
                    fontSize: '0.8rem',
                    bgcolor: '#6871d1',
                    px: 1.2,
                    py: 0.2,
                    borderRadius: 4,
                    verticalAlign: 'middle',
                  }}
                >
                  {pagination.totalTasks}
                </Box>
              </Typography>
            </Box>

            <Stack direction='row' spacing={2} sx={{ alignItems: 'center' }}>
              {/* Period Filter */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'rgba(43, 44, 62, 0.6)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  px: 2,
                  height: 44,
                  transition: 'all 0.2s',
                  '&:hover': {
                    border: '1px solid rgba(104, 113, 209, 0.5)',
                    bgcolor: 'rgba(43, 44, 62, 0.9)',
                  },
                }}
              >
                <CalendarMonthIcon sx={{ color: '#6871d1', fontSize: 20, mr: 1.5 }} />
                <Select
                  value={selectedPeriodFilter}
                  onChange={(e) => setSelectedPeriodFilter(e.target.value as any)}
                  disableUnderline
                  variant='standard'
                  displayEmpty
                  sx={{
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    minWidth: 100,
                    '& .MuiSelect-icon': { color: '#8f90a6' },
                    '& .MuiSelect-select': { pr: '24px !important' },
                  }}
                >
                  <MenuItem value='all'>All Time</MenuItem>
                  <MenuItem value='weekly'>This Week</MenuItem>
                  <MenuItem value='monthly'>This Month</MenuItem>
                </Select>
              </Box>

              {/* User Filter */}
              {isSuperAdmin && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(43, 44, 62, 0.6)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.08)',
                    px: 2,
                    height: 44,
                    transition: 'all 0.2s',
                    '&:hover': {
                      border: '1px solid rgba(104, 113, 209, 0.5)',
                      bgcolor: 'rgba(43, 44, 62, 0.9)',
                    },
                  }}
                >
                  <PersonIcon sx={{ color: '#6871d1', fontSize: 20, mr: 1.5 }} />
                  <Select
                    value={selectedUserFilter}
                    onChange={(e) => setSelectedUserFilter(e.target.value)}
                    disableUnderline
                    variant='standard'
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) return 'All Users';
                      const user = users.find((u) => u._id === selected);
                      return user ? capitalizeFirstLetter(user.username) : 'All Users';
                    }}
                    sx={{
                      color: '#fff',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      minWidth: 120,
                      '& .MuiSelect-icon': { color: '#8f90a6' },
                      '& .MuiSelect-select': { pr: '24px !important' },
                    }}
                  >
                    <MenuItem value=''>All Users</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user._id} value={user._id}>
                        {capitalizeFirstLetter(user.username)}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
              {(selectedPeriodFilter !== 'all' || selectedUserFilter !== '') && (
                <Button
                  size='small'
                  onClick={() => {
                    setSelectedPeriodFilter('all');
                    setSelectedUserFilter('');
                  }}
                  startIcon={<CancelIcon />}
                  sx={{
                    color: '#ff5252',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                    borderRadius: '12px',
                    '&:hover': { bgcolor: 'rgba(255, 82, 82, 0.1)' },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>
          </Box>

          {/* Column Headers */}
          <Grid
            container
            sx={{
              px: 3,
              py: 1.5,
              color: '#8f90a6',
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              mb: 1,
            }}
          >
            <Grid item xs={12} md={4}>
              Task Details
            </Grid>
            <Grid item xs={6} md={2}>
              Category
            </Grid>
            <Grid item xs={6} md={2}>
              Assignee
            </Grid>
            <Grid item xs={6} md={2}>
              Status
            </Grid>
            <Grid item xs={6} md={2} sx={{ textAlign: 'right' }}>
              Actions
            </Grid>
          </Grid>

          {/* List */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <AnimatePresence mode='popLayout'>
              {filteredTasks.map((task, index) => (
                <motion.div
                  key={task._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.25, delay: index * 0.03 }}
                  layout
                >
                  <Paper
                    onClick={() => handleViewTask(task)}
                    sx={{
                      p: 2.5, // Increased padding for clearer separation
                      mb: 1.5, // Spacing between rows
                      bgcolor: 'transparent', // Use gradient in background
                      background:
                        'linear-gradient(145deg, rgba(40, 41, 60, 0.7) 0%, rgba(30, 30, 45, 0.9) 100%)',
                      backdropFilter: 'blur(12px)',
                      borderRadius: 3,
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid rgba(255, 255, 255, 0.04)',
                      borderLeft: `4px solid ${getStatusColor(task.status)}`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      '&:hover': {
                        background:
                          'linear-gradient(145deg, rgba(45, 46, 65, 0.9) 0%, rgba(35, 35, 55, 1) 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        borderColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Grid container alignItems='center' spacing={2}>
                      {/* Title Section */}
                      <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            variant='subtitle1'
                            sx={{
                              fontWeight: 600,
                              color: '#fff',
                              fontSize: '0.95rem',
                              letterSpacing: '0.01em',
                              lineHeight: 1.4,
                              mb: 0.8,
                              transition: 'color 0.2s',
                            }}
                          >
                            {task.title}
                          </Typography>
                          <Stack direction='row' alignItems='center' spacing={1}>
                            {/* Due Date Indicator - Fixed Logic */}
                            {(() => {
                              const isOverdue = dayjs(task.dueDate).endOf('day').isBefore(dayjs());
                              return (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.6,
                                    color: isOverdue ? '#ff5252' : '#9ea0b5',
                                    bgcolor: isOverdue ? 'rgba(255, 82, 82, 0.1)' : 'transparent',
                                    px: isOverdue ? 1 : 0,
                                    py: isOverdue ? 0.25 : 0,
                                    borderRadius: 1,
                                  }}
                                >
                                  <AccessTimeIcon sx={{ fontSize: 13 }} />
                                  <Typography
                                    variant='caption'
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '0.75rem',
                                      letterSpacing: '0.02em',
                                    }}
                                  >
                                    {formatDate(task.dueDate)}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </Stack>
                        </Box>
                      </Grid>

                      {/* Category */}
                      <Grid item xs={6} md={2}>
                        <Chip
                          label={task.category === 'recurring_task' ? 'Recurring' : task.category}
                          size='small'
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.06)',
                            color: '#e0e0ee',
                            fontWeight: 500,
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            height: 26,
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            backdropFilter: 'blur(4px)',
                          }}
                        />
                      </Grid>

                      {/* Assigned To - Refined */}
                      <Grid item xs={6} md={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {task.assignedTo.length === 1 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  bgcolor: getAvatarColor(task.assignedTo[0].username),
                                  border: '2px solid rgba(43, 44, 62, 1)',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                }}
                                alt={task.assignedTo[0].username}
                              >
                                {capitalizeFirstLetter(task.assignedTo[0].username).charAt(0)}
                              </Avatar>
                              <Typography
                                variant='body2'
                                sx={{ color: '#e0e0ee', fontWeight: 500, fontSize: '0.85rem' }}
                              >
                                {capitalizeFirstLetter(task.assignedTo[0].username)}
                              </Typography>
                            </Box>
                          ) : (
                            <AvatarGroup
                              max={4}
                              sx={{
                                justifyContent: 'flex-start',
                                '& .MuiAvatar-root': {
                                  width: 32,
                                  height: 32,
                                  fontSize: '0.8rem',
                                  border: '2px solid rgba(43, 44, 62, 1)',
                                },
                              }}
                            >
                              {task.assignedTo.map((user) => (
                                <Tooltip
                                  key={user._id}
                                  title={capitalizeFirstLetter(user.username)}
                                >
                                  <Avatar
                                    sx={{ bgcolor: getAvatarColor(user.username) }}
                                    alt={user.username}
                                  >
                                    {capitalizeFirstLetter(user.username).charAt(0)}
                                  </Avatar>
                                </Tooltip>
                              ))}
                            </AvatarGroup>
                          )}
                        </Box>
                      </Grid>

                      {/* Status */}
                      <Grid item xs={6} md={2}>
                        {(() => {
                          const statusStyle = getStatusChipColor(task.status);
                          return (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 0.6,
                                borderRadius: '20px',
                                bgcolor: statusStyle.bg,
                                border: `1px solid ${statusStyle.border}`,
                                color: statusStyle.color,
                                minWidth: 110,
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  filter: 'brightness(1.1)',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'currentColor',
                                  boxShadow: '0 0 8px currentColor',
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.08em',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {task.status === 'pending_approval'
                                  ? 'Pending Approval'
                                  : task.status.replace('_', ' ')}
                              </Typography>
                            </Box>
                          );
                        })()}
                      </Grid>

                      {/* Actions Group - Clean & Minimal */}
                      <Grid item xs={6} md={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          {task.status === 'pending_approval' && isAdminOrSales && (
                            <Tooltip title='Approve'>
                              <IconButton
                                size='small'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApproveTask(task._id);
                                }}
                                disabled={isSales}
                                sx={{
                                  color: '#4caf50',
                                  bgcolor: 'rgba(76, 175, 80, 0.1)',
                                  border: '1px solid rgba(76, 175, 80, 0.2)',
                                  '&:hover': { bgcolor: '#4caf50', color: '#fff' },
                                  '&.Mui-disabled': { opacity: 0.3 },
                                }}
                              >
                                <ThumbUpIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          )}

                          {task.category !== 'Email' &&
                            task.category !== 'Other' &&
                            task.category !== 'recurring_task' && (
                              <Tooltip title='Checklist'>
                                <IconButton
                                  size='small'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingTask(task);
                                    setOpenChecklistDialog(true);
                                  }}
                                  sx={{
                                    color: '#8f90a6',
                                    opacity: 0.8,
                                    '&:hover': {
                                      color: '#4caf50',
                                      bgcolor: 'rgba(76, 175, 80, 0.1)',
                                      opacity: 1,
                                    },
                                  }}
                                >
                                  <ChecklistIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            )}

                          <Tooltip title='View details'>
                            <IconButton
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewTask(task);
                              }}
                              sx={{
                                color: '#8f90a6',
                                opacity: 0.8,
                                '&:hover': {
                                  color: '#fff',
                                  bgcolor: 'rgba(255,255,255,0.1)',
                                  opacity: 1,
                                },
                              }}
                            >
                              <VisibilityIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>

                          {isAdminOrSales && (
                            <>
                              <Tooltip title='Edit'>
                                <IconButton
                                  size='small'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditTask(task);
                                  }}
                                  disabled={
                                    task.status === 'approved' || task.status === 'completed'
                                  }
                                  sx={{
                                    color: '#8f90a6',
                                    opacity: 0.8,
                                    '&:hover': {
                                      color: '#6871d1',
                                      bgcolor: 'rgba(104, 113, 209, 0.1)',
                                      opacity: 1,
                                    },
                                    '&.Mui-disabled': { opacity: 0.3 },
                                  }}
                                >
                                  <EditIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>

                              {!isSales && (
                                <Tooltip title='Delete'>
                                  <IconButton
                                    size='small'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteTaskClick(task._id);
                                    }}
                                    sx={{
                                      color: '#8f90a6',
                                      opacity: 0.8,
                                      '&:hover': {
                                        color: '#ff5252',
                                        bgcolor: 'rgba(244, 67, 54, 0.1)',
                                        opacity: 1,
                                      },
                                      '&.Mui-disabled': { opacity: 0.3 },
                                    }}
                                  >
                                    <DeleteIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </motion.div>
              ))}
            </AnimatePresence>
            {filteredTasks.length === 0 && (
              <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                sx={{
                  textAlign: 'center',
                  py: 8,
                  bgcolor: 'rgba(255,255,255,0.02)',
                  borderRadius: 4,
                }}
              >
                <Typography variant='h6' color='textSecondary'>
                  No tasks found
                </Typography>
              </Box>
            )}
          </Box>

          {/* Pagination UI */}
          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination
                count={pagination.totalPages}
                page={currentPage}
                onChange={(_, value) => fetchTasks(value)}
                color='primary'
                size='large'
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(104, 113, 209, 0.1)',
                    },
                    '&.Mui-selected': {
                      bgcolor: '#6871d1',
                      color: '#fff',
                      fontWeight: 700,
                      boxShadow: '0 4px 12px rgba(104, 113, 209, 0.3)',
                      '&:hover': {
                        bgcolor: '#5a63bb',
                      },
                    },
                  },
                  '& .MuiPaginationItem-ellipsis': {
                    color: 'rgba(255, 255, 255, 0.4)',
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Create/Edit Task Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #1e1e2f 0%, #2b2c3e 100%)',
            color: '#fff',
            borderRadius: 4,
            boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
            border: '1px solid rgba(104, 113, 209, 0.2)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'rgba(30, 30, 45, 0.4)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.25rem',
            py: 2.5,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(104, 113, 209, 0.1)',
              p: 0.8,
              borderRadius: 2,
              color: '#6871d1',
              display: 'flex',
            }}
          >
            {displayDialogMode === 'create' ? <AddIcon /> : <EditIcon />}
          </Box>
          {displayDialogMode === 'create'
            ? 'Create New Task'
            : displayDialogMode === 'edit'
              ? 'Edit Task'
              : 'View Task'}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: 3 }}>
            <TextField
              label='Title'
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
              disabled={
                dialogMode === 'view' ||
                (dialogMode === 'edit' &&
                  (!isAdminOrSales ||
                    editingTask?.status === 'approved' ||
                    editingTask?.status === 'completed'))
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.08)' },
                  '&:hover fieldset': { borderColor: 'rgba(104, 113, 209, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                  color: '#fff',
                },
                '& .MuiInputLabel-root': { color: '#8f90a6' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#6871d1' },
                '& .Mui-disabled': { opacity: 0.6, WebkitTextFillColor: '#8f90a6 !important' },
              }}
            />

            <FormControl fullWidth>
              <InputLabel sx={{ color: '#8f90a6', '&.Mui-focused': { color: '#6871d1' } }}>
                Category
              </InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                label='Category'
                disabled={
                  dialogMode === 'view' ||
                  (dialogMode === 'edit' &&
                    (!isAdminOrSales ||
                      editingTask?.status === 'approved' ||
                      editingTask?.status === 'completed'))
                }
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: 2,
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(104, 113, 209, 0.3)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6871d1' },
                  '& .MuiSvgIcon-root': { color: '#8f90a6' },
                }}
              >
                <MenuItem value='Linkedin Post'>Linkedin Post</MenuItem>
                <MenuItem value='Blog'>Blog</MenuItem>
                <MenuItem value='Twitter Post'>Twitter Post</MenuItem>
                <MenuItem value='Email'>Email</MenuItem>
                <MenuItem value='Other'>Other</MenuItem>
                <MenuItem value='recurring_task'>Recurring Task</MenuItem>
              </Select>
            </FormControl>

            {formData.category === 'recurring_task' && (
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#8f90a6', '&.Mui-focused': { color: '#6871d1' } }}>
                  Recurrence Options
                </InputLabel>
                <Select
                  value={formData.recurrenceType || 'no_change'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      recurrenceType: e.target.value as 'no_change' | '1st' | '5th' | '10th',
                    })
                  }
                  label='Recurrence Options'
                  disabled={
                    dialogMode === 'view' ||
                    (dialogMode === 'edit' &&
                      (!isAdminOrSales ||
                        editingTask?.status === 'approved' ||
                        editingTask?.status === 'completed'))
                  }
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: 2,
                    color: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.08)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(104, 113, 209, 0.3)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6871d1' },
                    '& .MuiSvgIcon-root': { color: '#8f90a6' },
                  }}
                >
                  <MenuItem value='no_change'>No change</MenuItem>
                  <MenuItem value='1st'>1st of month</MenuItem>
                  <MenuItem value='5th'>5th of month</MenuItem>
                  <MenuItem value='10th'>10th of month</MenuItem>
                </Select>
                <Typography
                  variant='caption'
                  sx={{
                    mt: 1,
                    color: '#6871d1',
                    fontStyle: 'italic',
                    display: 'block',
                  }}
                >
                  {formData.recurrenceType === '1st'
                    ? 'This task will automatically recreate on the 1st of every month after completion'
                    : formData.recurrenceType === '5th'
                      ? 'This task will automatically recreate on the 5th of every month after completion'
                      : formData.recurrenceType === '10th'
                        ? 'This task will automatically recreate on the 10th of every month after completion'
                        : 'This task will automatically recreate every Monday after completion'}
                </Typography>
              </FormControl>
            )}
            {formData.category === 'Other' && isSuperAdmin && dialogMode !== 'view' && (
              <Box
                sx={{
                  mt: 2,
                  mb: 1,
                  p: '10px 14px',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.23)', // Matches standard MUI OutlinedInput default
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderColor: '#fff', // Matches standard MUI hover
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant='body1' sx={{ color: '#fff', fontSize: '1rem' }}>
                    Require Proof Upload
                  </Typography>
                  <Typography variant='caption' sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
                    {formData.bypassUpload
                      ? 'Direct Task (No proof required)'
                      : 'User must upload SEO image/SOP'}
                  </Typography>
                </Box>
                <Switch
                  checked={!formData.bypassUpload}
                  onChange={(e) => setFormData({ ...formData, bypassUpload: !e.target.checked })}
                  color='primary'
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#6871d1',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#6871d1',
                    },
                  }}
                />
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel sx={{ color: '#8f90a6', '&.Mui-focused': { color: '#6871d1' } }}>
                Assigned To
              </InputLabel>
              <Select
                multiple
                value={formData.assignedTo}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    assignedTo:
                      typeof e.target.value === 'string' ? [e.target.value] : e.target.value,
                  })
                }
                label='Assigned To'
                disabled={
                  dialogMode === 'view' ||
                  (dialogMode === 'edit' &&
                    (!isAdminOrSales ||
                      editingTask?.status === 'approved' ||
                      editingTask?.status === 'completed'))
                }
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: 2,
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(104, 113, 209, 0.3)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6871d1' },
                  '& .MuiSvgIcon-root': { color: '#8f90a6' },
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((userId) => {
                      const user = users.find((u) => u._id === userId);
                      return (
                        <Chip
                          key={userId}
                          label={user ? capitalizeFirstLetter(user.username) : userId}
                          size='small'
                          sx={{ bgcolor: 'rgba(104, 113, 209, 0.2)', color: '#fff' }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {loadingUsers ? (
                  <MenuItem disabled>
                    <Typography variant='body2' color='textSecondary'>
                      Loading users...
                    </Typography>
                  </MenuItem>
                ) : users.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant='body2' color='textSecondary'>
                      No users available
                    </Typography>
                  </MenuItem>
                ) : (
                  users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
                          {capitalizeFirstLetter(user.username)}
                        </Typography>
                        <Typography variant='caption' color='textSecondary'>
                          {user.email} • {user.role}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
              {!loadingUsers && users.length === 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant='caption' color='textSecondary'>
                    No users available. Please check your connection.
                  </Typography>
                </Box>
              )}
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='Due Date'
                value={formData.dueDate ? dayjs(formData.dueDate) : null}
                onChange={(date) =>
                  setFormData({
                    ...formData,
                    dueDate: date ? dayjs(date).format('YYYY-MM-DD') : '',
                  })
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    disabled:
                      dialogMode === 'view' ||
                      (dialogMode === 'edit' &&
                        (!isAdminOrSales ||
                          editingTask?.status === 'approved' ||
                          editingTask?.status === 'completed')),
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: 2,
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.08)' },
                        '&:hover fieldset': { borderColor: 'rgba(104, 113, 209, 0.3)' },
                        '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                        color: '#fff',
                      },
                      '& .MuiInputLabel-root': { color: '#8f90a6' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#6871d1' },
                      '& .MuiSvgIcon-root': { color: '#8f90a6' },
                      '& .Mui-disabled': {
                        opacity: 0.6,
                        WebkitTextFillColor: '#8f90a6 !important',
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              label='Content'
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              fullWidth
              multiline
              rows={4}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.08)' },
                  '&:hover fieldset': { borderColor: 'rgba(104, 113, 209, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                  color: '#fff',
                },
                '& .MuiInputLabel-root': { color: '#8f90a6' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#6871d1' },
                '& .Mui-disabled': { opacity: 0.6, WebkitTextFillColor: '#8f90a6 !important' },
              }}
              disabled={
                dialogMode === 'view' ||
                (dialogMode === 'edit' &&
                  (!isAdminOrSales ||
                    editingTask?.status === 'approved' ||
                    editingTask?.status === 'completed'))
              }
            />

            {/* Task Details Section - Only show in view mode */}
            {dialogMode === 'view' && editingTask && (
              <Box
                sx={{
                  mt: 2,
                  alignItems: 'center',
                  p: 2.5,
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.02)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    pb: 1.5,
                  }}
                >
                  <Typography variant='h6' sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    Task Details
                  </Typography>
                  {!isEditingViewTaskDetails &&
                    isSuperAdmin &&
                    (editingTask?.seoScoreImage || editingTask?.driveUrl) && (
                      <Tooltip
                        title={
                          editingTask?.status === 'approved' || editingTask?.status === 'completed'
                            ? 'Cannot edit approved/completed tasks'
                            : 'Edit Task Details'
                        }
                      >
                        <IconButton
                          onClick={handleEditViewTaskDetails}
                          disabled={
                            loadingChecklist ||
                            editingTask?.status === 'approved' ||
                            editingTask?.status === 'completed'
                          }
                          sx={{
                            backgroundColor: 'rgba(104, 113, 209, 0.1)',
                            color: '#6871d1',
                            '&:hover': {
                              backgroundColor: 'rgba(104, 113, 209, 0.2)',
                            },
                            '&:disabled': {
                              backgroundColor: 'rgba(255,255,255,0.05)',
                              color: '#aaa',
                              opacity: 0.7,
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Recurring Task Information Section */}

                  {/* Task Progress Section - Only show for non-Email/Other/Recurring categories */}
                  {editingTask?.category !== 'Email' &&
                    editingTask?.category !== 'Other' &&
                    editingTask?.category !== 'recurring_task' &&
                    (loadingChecklist ? (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant='body2' color='textSecondary'>
                          Loading checklist...
                        </Typography>
                      </Box>
                    ) : viewTaskChecklist && viewTaskChecklist.length > 0 ? (
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                          }}
                        >
                          <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Progress
                          </Typography>
                          <Chip
                            label={`${viewTaskChecklist.filter((item) => item.isCompleted).length}/${viewTaskChecklist.length} completed`}
                            color={
                              viewTaskChecklist.filter((item) => item.isCompleted).length ===
                              viewTaskChecklist.length
                                ? 'success'
                                : 'default'
                            }
                            size='small'
                            sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600 }}
                          />
                        </Box>
                        <LinearProgress
                          variant='determinate'
                          value={
                            viewTaskChecklist.length > 0
                              ? (viewTaskChecklist.filter((item) => item.isCompleted).length /
                                  viewTaskChecklist.length) *
                                100
                              : 0
                          }
                          color={
                            viewTaskChecklist.filter((item) => item.isCompleted).length ===
                            viewTaskChecklist.length
                              ? 'success'
                              : 'primary'
                          }
                          sx={{
                            height: 6,
                            borderRadius: 4,
                            mb: 0.5,
                            bgcolor: 'rgba(255,255,255,0.1)',
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                          <Typography variant='body2' color='textSecondary'>
                            {Math.round(
                              (viewTaskChecklist.filter((item) => item.isCompleted).length /
                                viewTaskChecklist.length) *
                                100
                            )}
                            %
                          </Typography>
                        </Box>
                        {/* Checklist Items */}
                        <List
                          dense
                          sx={{ bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2, overflow: 'hidden' }}
                        >
                          {viewTaskChecklist.map((item) => (
                            <ListItem
                              key={item.id}
                              disablePadding
                              sx={{
                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                '&:last-child': { borderBottom: 'none' },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36, pl: 2 }}>
                                {item.isCompleted ? (
                                  <CheckCircleIcon color='success' fontSize='small' />
                                ) : (
                                  <ChecklistIcon
                                    fontSize='small'
                                    sx={{ color: 'text.secondary' }}
                                  />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant='body2'
                                    sx={{
                                      textDecoration: item.isCompleted ? 'line-through' : 'none',
                                      color: item.isCompleted ? 'text.secondary' : 'text.primary',
                                    }}
                                  >
                                    {item.title}
                                  </Typography>
                                }
                                secondary={
                                  <>
                                    <Typography
                                      variant='caption'
                                      color='textSecondary'
                                      sx={{
                                        textDecoration: item.isCompleted ? 'line-through' : 'none',
                                      }}
                                    >
                                      {item.description}
                                    </Typography>
                                    {item.isCompleted && item.completedAt && (
                                      <Typography
                                        variant='caption'
                                        color='success.main'
                                        sx={{ display: 'block', mt: 0.5 }}
                                      >
                                        Completed on{' '}
                                        {dayjs(item.completedAt).format('MMM DD, YYYY HH:mm')}
                                      </Typography>
                                    )}
                                  </>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ) : null)}

                  {/* Direct Task Completion Section - Show ONLY button for bypass tasks */}
                  {(editingTask?.category === 'Email' ||
                    editingTask?.category === 'Other' ||
                    editingTask?.category === 'recurring_task') &&
                    editingTask?.status === 'pending' &&
                    editingTask?.bypassUpload && (
                      <Box sx={{ mt: 3 }}>
                        <Button
                          variant='contained'
                          fullWidth
                          onClick={async () => {
                            try {
                              setLoadingChecklist(true);
                              await ContentTrackingService.updateTaskStatus(editingTask._id, {
                                status: 'completed',
                              });
                              showSnackbar('Task completed successfully', 'success');
                              await fetchTasks();
                              handleCloseDialog();
                            } catch (error: any) {
                              console.error('Error completing task:', error);
                              showSnackbar(
                                error.response?.data?.message || 'Failed to complete task',
                                'error'
                              );
                            } finally {
                              setLoadingChecklist(false);
                            }
                          }}
                          disabled={loadingChecklist}
                          startIcon={<CheckCircleIcon />}
                          sx={{
                            backgroundColor: '#4caf50',
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '1rem',
                            py: 1.5,
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                            '&:hover': {
                              backgroundColor: '#43a047',
                              boxShadow: '0 6px 16px rgba(76, 175, 80, 0.5)',
                            },
                          }}
                        >
                          Complete Task
                        </Button>
                      </Box>
                    )}

                  {/* Standard Upload Content Section - Show for non-bypass tasks */}
                  {(editingTask?.category === 'Email' ||
                    editingTask?.category === 'Other' ||
                    editingTask?.category === 'recurring_task') &&
                    editingTask?.status === 'pending' &&
                    !editingTask?.bypassUpload && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 3,
                          backgroundColor: '#1e1e2f',
                          borderRadius: 3,
                          border: '1px solid rgba(104, 113, 209, 0.3)',
                          boxShadow: '0 4px 12px rgba(104, 113, 209, 0.1)',
                        }}
                      >
                        <Typography
                          variant='h6'
                          sx={{
                            mb: 2.5,
                            color: '#6871d1',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <UploadIcon sx={{ fontSize: 20 }} />
                          Upload Content
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                          <Typography
                            variant='body2'
                            sx={{
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '0.9rem',
                              lineHeight: 1.6,
                            }}
                          >
                            Upload image and provide drive URL to request approval.
                          </Typography>

                          <Box>
                            <input
                              accept='image/*'
                              style={{ display: 'none' }}
                              id='view-seo-image-upload'
                              type='file'
                              onChange={handleEditViewImageUpload}
                            />
                            <label htmlFor='view-seo-image-upload'>
                              <Button
                                variant='outlined'
                                component='span'
                                startIcon={<UploadIcon />}
                                disabled={loadingChecklist}
                                sx={{
                                  borderColor: 'rgba(104, 113, 209, 0.5)',
                                  color: '#6871d1',
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  px: 3,
                                  py: 1,
                                  '&:hover': {
                                    borderColor: '#6871d1',
                                    backgroundColor: 'rgba(104, 113, 209, 0.1)',
                                  },
                                }}
                              >
                                {editingViewSeoImage ? editingViewSeoImage.name : 'Upload Image'}
                              </Button>
                            </label>
                            {editingViewSeoImage && (
                              <Typography
                                variant='caption'
                                sx={{
                                  color: '#4caf50',
                                  ml: 2,
                                  fontWeight: 500,
                                  fontSize: '0.875rem',
                                }}
                              >
                                ✓ {editingViewSeoImage.name}
                              </Typography>
                            )}
                          </Box>

                          {editingTask?.category !== 'Email' && (
                            <TextField
                              label='Drive URL'
                              value={editingViewDriveUrl}
                              onChange={(e) => setEditingViewDriveUrl(e.target.value)}
                              fullWidth
                              placeholder='https://drive.google.com/...'
                              disabled={loadingChecklist}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  color: '#fff',
                                  '& fieldset': {
                                    borderColor: 'rgba(104, 113, 209, 0.3)',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#6871d1',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#6871d1',
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: 'rgba(255, 255, 255, 0.7)',
                                },
                              }}
                            />
                          )}

                          <Button
                            variant='contained'
                            onClick={async () => {
                              if (!editingTask || !editingViewSeoImage) {
                                showSnackbar('Please upload an image', 'error');
                                return;
                              }
                              try {
                                setLoadingChecklist(true);
                                await ContentTrackingService.requestApproval(editingTask._id, {
                                  seoImage: editingViewSeoImage,
                                  driveUrl: editingViewDriveUrl.trim(),
                                });
                                showSnackbar('Approval requested successfully', 'success');
                                await fetchTasks(currentPage);
                                // Refresh the task data
                                const updatedTasks = await ContentTrackingService.getAllTasks();
                                if (updatedTasks.status) {
                                  const updatedTask = updatedTasks.data.find(
                                    (t) => t._id === editingTask._id
                                  );
                                  if (updatedTask) {
                                    setEditingTask(updatedTask);
                                    setEditingViewSeoImage(null);
                                    setEditingViewDriveUrl('');
                                  }
                                }
                              } catch (error: any) {
                                console.error('Error requesting approval:', error);
                                if (error.response?.data?.message) {
                                  showSnackbar(error.response.data.message, 'error');
                                } else if (error.message) {
                                  showSnackbar(error.message, 'error');
                                } else {
                                  showSnackbar('Failed to request approval', 'error');
                                }
                              } finally {
                                setLoadingChecklist(false);
                              }
                            }}
                            disabled={!editingViewSeoImage || loadingChecklist}
                            startIcon={<SendIcon />}
                            sx={{
                              backgroundColor: '#6871d1',
                              color: '#fff',
                              textTransform: 'none',
                              fontWeight: 600,
                              py: 1.5,
                              fontSize: '0.95rem',
                              boxShadow: '0 4px 12px rgba(104, 113, 209, 0.3)',
                              '&:hover': {
                                backgroundColor: '#5a63bb',
                                boxShadow: '0 6px 16px rgba(104, 113, 209, 0.4)',
                              },
                              '&:disabled': {
                                backgroundColor: 'rgba(104, 113, 209, 0.3)',
                                color: 'rgba(255, 255, 255, 0.5)',
                              },
                            }}
                          >
                            Request Approval
                          </Button>
                        </Box>
                      </Box>
                    )}

                  {/* SEO Image Section */}
                  {(editingTask?.seoScoreImage || isEditingViewTaskDetails) && (
                    <Box
                      sx={{
                        mt: 3,
                        p: 2.5,
                        backgroundColor: '#1e1e2f',
                        borderRadius: 3,
                        border: '1px solid rgba(104, 113, 209, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'rgba(104, 113, 209, 0.4)',
                          boxShadow: '0 4px 12px rgba(104, 113, 209, 0.15)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            backgroundColor: 'rgba(104, 113, 209, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ImageIcon sx={{ color: '#6871d1', fontSize: 20 }} />
                        </Box>
                        <Typography
                          variant='subtitle2'
                          sx={{
                            fontWeight: 700,
                            color: '#6871d1',
                            fontSize: '0.95rem',
                            letterSpacing: 0.5,
                          }}
                        >
                          Image
                        </Typography>
                      </Box>
                      {!isEditingViewTaskDetails ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 2,
                            backgroundColor: '#23234a',
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                          }}
                        >
                          <img
                            src={editingTask?.seoScoreImage}
                            alt='SEO Score'
                            style={{
                              maxWidth: '100%',
                              maxHeight: '250px',
                              borderRadius: '8px',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {editingTask?.seoScoreImage && !editingViewSeoImage && (
                            <Box>
                              <img
                                src={editingTask.seoScoreImage}
                                alt='Current SEO Score'
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '200px',
                                  borderRadius: '8px',
                                  border: '1px solid #ddd',
                                  marginBottom: '8px',
                                }}
                              />
                              <Typography variant='caption' color='textSecondary'>
                                Current image (upload new to replace)
                              </Typography>
                            </Box>
                          )}
                          {editingViewSeoImage && (
                            <Box>
                              <img
                                src={URL.createObjectURL(editingViewSeoImage)}
                                alt='New SEO Score Preview'
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '200px',
                                  borderRadius: '8px',
                                  border: '1px solid #4caf50',
                                  marginBottom: '8px',
                                }}
                              />
                              <Typography variant='caption' color='success.main'>
                                New image: {editingViewSeoImage.name}
                              </Typography>
                            </Box>
                          )}
                          <input
                            accept='image/*'
                            style={{ display: 'none' }}
                            id='edit-view-seo-image-upload'
                            type='file'
                            onChange={handleEditViewImageUpload}
                          />
                          <label htmlFor='edit-view-seo-image-upload'>
                            <Button
                              variant='outlined'
                              component='span'
                              size='small'
                              startIcon={<UploadIcon />}
                              disabled={loadingChecklist}
                            >
                              {editingViewSeoImage ? 'Change Image' : 'Upload New Image'}
                            </Button>
                          </label>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Drive URL Section */}
                  {(editingTask?.driveUrl || isEditingViewTaskDetails) && (
                    <Box
                      sx={{
                        mt: 3,
                        p: 2.5,
                        backgroundColor: '#1e1e2f',
                        borderRadius: 3,
                        border: '1px solid rgba(104, 113, 209, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'rgba(104, 113, 209, 0.4)',
                          boxShadow: '0 4px 12px rgba(104, 113, 209, 0.15)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            backgroundColor: 'rgba(104, 113, 209, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <LinkIcon sx={{ color: '#6871d1', fontSize: 20 }} />
                        </Box>
                        <Typography
                          variant='subtitle2'
                          sx={{
                            fontWeight: 700,
                            color: '#6871d1',
                            fontSize: '0.95rem',
                            letterSpacing: 0.5,
                          }}
                        >
                          Drive URL
                        </Typography>
                      </Box>
                      {!isEditingViewTaskDetails ? (
                        <Button
                          component='a'
                          href={editingTask?.driveUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          variant='outlined'
                          endIcon={<OpenInNewIcon />}
                          sx={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            color: '#fff',
                            borderColor: 'rgba(104, 113, 209, 0.5)',
                            backgroundColor: 'rgba(104, 113, 209, 0.08)',
                            py: 1.5,
                            px: 2,
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              borderColor: '#6871d1',
                              backgroundColor: 'rgba(104, 113, 209, 0.15)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(104, 113, 209, 0.25)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {editingTask?.driveUrl}
                        </Button>
                      ) : (
                        <TextField
                          label='Drive URL'
                          value={editingViewDriveUrl}
                          onChange={(e) => setEditingViewDriveUrl(e.target.value)}
                          fullWidth
                          placeholder='https://drive.google.com/...'
                          disabled={loadingChecklist}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: '#fff',
                              '& fieldset': {
                                borderColor: 'rgba(104, 113, 209, 0.5)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#6871d1',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#6871d1',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: '#aaa',
                            },
                          }}
                          helperText='Enter the Google Drive URL for this task'
                        />
                      )}
                    </Box>
                  )}

                  {/* Edit Mode Action Buttons */}
                  {isEditingViewTaskDetails && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'flex-end',
                        mt: 2,
                      }}
                    >
                      <Button
                        variant='outlined'
                        onClick={handleCancelEditViewTaskDetails}
                        disabled={loadingChecklist}
                        startIcon={<CancelIcon />}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant='contained'
                        color='primary'
                        onClick={handleSaveViewTaskDetails}
                        disabled={loadingChecklist}
                        startIcon={<SaveIcon />}
                      >
                        Save Changes
                      </Button>
                    </Box>
                  )}

                  {/* Posted URL Section */}
                  {editingTask?.postedUrl && (
                    <Box
                      sx={{
                        mt: 3,
                        p: 2.5,
                        backgroundColor: '#1e1e2f',
                        borderRadius: 3,
                        border: '1px solid rgba(104, 113, 209, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'rgba(104, 113, 209, 0.4)',
                          boxShadow: '0 4px 12px rgba(104, 113, 209, 0.15)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            backgroundColor: 'rgba(104, 113, 209, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ArticleIcon sx={{ color: '#6871d1', fontSize: 20 }} />
                        </Box>
                        <Typography
                          variant='subtitle2'
                          sx={{
                            fontWeight: 700,
                            color: '#6871d1',
                            fontSize: '0.95rem',
                            letterSpacing: 0.5,
                          }}
                        >
                          Posted URL
                        </Typography>
                      </Box>
                      <Button
                        component='a'
                        href={editingTask.postedUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        variant='outlined'
                        endIcon={<OpenInNewIcon />}
                        sx={{
                          width: '100%',
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          color: '#fff',
                          borderColor: 'rgba(104, 113, 209, 0.5)',
                          backgroundColor: 'rgba(104, 113, 209, 0.08)',
                          py: 1.5,
                          px: 2,
                          borderRadius: 2,
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            borderColor: '#6871d1',
                            backgroundColor: 'rgba(104, 113, 209, 0.15)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(104, 113, 209, 0.25)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {editingTask.postedUrl}
                      </Button>
                    </Box>
                  )}

                  {/* Approval Section - Show when task is pending_approval and user is superadmin */}
                  {editingTask?.status === 'pending_approval' && isAdminOrSales && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant='h6' sx={{ mb: 2 }}>
                        Approve Task
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='body2' color='textSecondary'>
                          Review the submitted content and approve the task
                        </Typography>
                        <Button
                          variant='contained'
                          color='success'
                          onClick={async () => {
                            if (!editingTask) return;
                            try {
                              await ContentTrackingService.approveTask(editingTask._id, true);
                              showSnackbar('Task approved successfully', 'success');
                              fetchTasks();
                              // Refresh the task data
                              const updatedTasks = await ContentTrackingService.getAllTasks();
                              if (updatedTasks.status) {
                                const updatedTask = updatedTasks.data.find(
                                  (t) => t._id === editingTask._id
                                );
                                if (updatedTask) {
                                  setEditingTask(updatedTask);
                                  fetchChecklistForView(updatedTask);
                                }
                              }
                            } catch (error) {
                              console.error('Error approving task:', error);
                              showSnackbar('Failed to approve task', 'error');
                            }
                          }}
                          startIcon={<ThumbUpIcon />}
                          sx={{
                            backgroundColor: '#4caf50',
                            '&:hover': {
                              backgroundColor: '#45a049',
                            },
                          }}
                        >
                          Approve Task
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Complete Section - Show when task is approved */}
                  {editingTask?.status === 'approved' && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant='h6' sx={{ mb: 2 }}>
                        Complete Task
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant='body2' color='textSecondary'>
                          Task has been approved. Provide the final posted URL to complete.
                        </Typography>
                        <TextField
                          label='Posted URL'
                          value={postedUrlInput}
                          onChange={(e) => setPostedUrlInput(e.target.value)}
                          fullWidth
                          placeholder='https://linkedin.com/posts/...'
                        />
                        <Button
                          variant='contained'
                          color='success'
                          onClick={async () => {
                            if (!editingTask || !postedUrlInput.trim()) {
                              showSnackbar('Please provide the posted URL', 'error');
                              return;
                            }
                            try {
                              await ContentTrackingService.updateTask(editingTask._id, {
                                status: 'completed',
                                postedUrl: postedUrlInput.trim(),
                              });
                              showSnackbar('Task completed successfully', 'success');
                              fetchTasks();
                              // Refresh the task data
                              const updatedTasks = await ContentTrackingService.getAllTasks();
                              if (updatedTasks.status) {
                                const updatedTask = updatedTasks.data.find(
                                  (t) => t._id === editingTask._id
                                );
                                if (updatedTask) {
                                  setEditingTask(updatedTask);
                                  setPostedUrlInput(updatedTask.postedUrl || '');
                                  fetchChecklistForView(updatedTask);
                                }
                              }
                            } catch (error) {
                              console.error('Error completing task:', error);
                              showSnackbar('Failed to complete task', 'error');
                            }
                          }}
                          disabled={!postedUrlInput.trim() || !editingTask?.isApprove}
                          startIcon={<CheckCircleIcon />}
                          sx={{
                            backgroundColor: '#4caf50',
                            '&:hover': {
                              backgroundColor: '#45a049',
                            },
                          }}
                        >
                          Complete Task
                        </Button>
                        {!editingTask?.isApprove && (
                          <Alert severity='warning' sx={{ mt: 1 }}>
                            Task must be approved before it can be completed.
                          </Alert>
                        )}
                      </Box>
                    </Box>
                  )}

                  {!editingTask?.seoScoreImage &&
                    !editingTask?.driveUrl &&
                    !editingTask?.postedUrl &&
                    (!viewTaskChecklist || viewTaskChecklist.length === 0) &&
                    editingTask?.status !== 'pending_approval' &&
                    editingTask?.status !== 'approved' && (
                      <Typography variant='body2' color='textSecondary'>
                        No additional details available for this task.
                      </Typography>
                    )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ background: '#23234a' }}>
          {dialogMode !== 'view' && openDialog && (
            <Button
              onClick={handleCloseDialog}
              sx={{
                backgroundColor: 'transparent',
                color: '#6871d1',
                border: '1.5px solid #6871d1',
                py: 1.5,
                borderRadius: '12px',
                fontWeight: 500,
                transition: 'all 0.3s ease',
                px: 3,
                fontSize: '1rem',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(104,113,209,0.08)',
                  color: '#fff',
                  border: '1.5px solid #6871d1',
                },
                '&:disabled': {
                  backgroundColor: '#e0e3f7',
                  color: '#aaa',
                  opacity: 0.7,
                },
              }}
            >
              Cancel
            </Button>
          )}
          {dialogMode === 'view' && openDialog ? (
            <Button
              variant='contained'
              onClick={handleCloseDialog}
              sx={{
                backgroundColor: '#6871d1',
                color: '#fff',
                py: 1.5,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
                transition: 'all 0.3s ease',
                fontWeight: 500,
                px: 3,
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: '#5a63bb',
                  boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
                },
                '&:disabled': {
                  backgroundColor: '#aeb3e9',
                  color: '#fff',
                  opacity: 0.7,
                },
              }}
            >
              Close
            </Button>
          ) : (
            <Button
              onClick={dialogMode === 'edit' ? handleUpdateTask : handleCreateTask}
              variant='contained'
              disabled={
                !formData.title ||
                !formData.content ||
                isSubmitting ||
                (dialogMode === 'edit' &&
                  (!isAdminOrSales ||
                    editingTask?.status === 'approved' ||
                    editingTask?.status === 'completed'))
              }
              sx={{
                backgroundColor: '#6871d1',
                color: '#fff',
                py: 1.5,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
                transition: 'all 0.3s ease',
                fontWeight: 500,
                px: 3,
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: '#5a63bb',
                  boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
                },
                '&:disabled': {
                  backgroundColor: '#aeb3e9',
                  color: '#fff',
                  opacity: 0.7,
                },
              }}
            >
              {isSubmitting
                ? dialogMode === 'edit'
                  ? 'Updating...'
                  : 'Creating...'
                : dialogMode === 'edit'
                  ? 'Update Task'
                  : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Checklist Dialog */}
      <ChecklistDialog
        open={openChecklistDialog}
        onClose={() => {
          setOpenChecklistDialog(false);
          setEditingTask(null);
        }}
        task={editingTask}
        onTaskUpdate={fetchTasks}
      />

      {/* Delete Task Confirmation Dialog */}
      <Dialog
        open={deleteTaskDialogOpen}
        onClose={handleDeleteTaskCancel}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e2f',
            color: '#fff',
            borderRadius: 4,
            boxShadow: '0 20px 60px 0 rgba(0,0,0,0.9)',
            border: '1px solid rgba(244, 67, 54, 0.2)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: '#23234a',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2.5,
            px: 3,
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            m: 0,
          }}
        >
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(244, 67, 54, 0.3)',
            }}
          >
            <DeleteIcon sx={{ color: '#f44336', fontSize: 22 }} />
          </Box>
          <Typography
            variant='h6'
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: '1.25rem',
              letterSpacing: 0.3,
            }}
          >
            Delete Task
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ background: '#1e1e2f', pt: 3.5, pb: 2, px: 3 }}>
          <Typography
            variant='body1'
            sx={{
              color: '#fff',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              fontWeight: 400,
              mb: 2.5,
            }}
          >
            Are you sure you want to delete this task? This action cannot be undone and all
            associated data will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            background: '#23234a',
            p: 2.5,
            gap: 1.5,
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            m: 0,
          }}
        >
          <Button
            onClick={handleDeleteTaskCancel}
            sx={{
              backgroundColor: 'transparent',
              color: '#aaa',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              py: 1.25,
              borderRadius: '8px',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              px: 3,
              fontSize: '0.95rem',
              textTransform: 'none',
              minWidth: 100,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTaskConfirm}
            variant='contained'
            startIcon={<DeleteIcon />}
            sx={{
              backgroundColor: '#f44336',
              color: '#fff',
              py: 1.25,
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
              transition: 'all 0.2s ease',
              fontWeight: 600,
              px: 3,
              fontSize: '0.95rem',
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                backgroundColor: '#d32f2f',
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.5)',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentTracking;
